/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import type { TranslatedString } from "@i18n";
import React, { useRef, useState, useEffect, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { taskInspectionAndProgressFormUrl } from "@Services/dataManager";
import { taskInspectionAndProgressFormsController, formUserSettingsController } from "@Actions/Forms";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { formCategories, presetSectionsCodeObject, getInspectionUrl } from "@Helpers";
import InspectionCreation from "./InspectionCreation";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { formStatuses, formStatusMapper } from "./constants";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { DisabledIllustration } from "@Features/commercial-shared/contracts/contractHelpers";
import Badge from "@mui/material/Badge";
import useFromList from "./useFromList";
import Text from "@CubeComponents/html/Text/Text";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { Box } from "@mui/material";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { GridClickEvent, ToolbarItem, ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid1";
import type { FormInstance } from "../../formInstance.wire";

/**
 * A row of the forms list, as `taskInspectionAndProgressFormsController`'s list action returns them.
 *
 * That response is `result: z.array(z.any())`, so there is no server row type to import. The fields
 * are therefore named here, with their types taken from `FormInstance` wherever the row carries a
 * whole instance sub-object (`space`, `selectedForm`, `lastAction`, …) so those cannot drift from the
 * stored shape. The remaining fields (`currentLevelName`, `contractOrgName`, …) are grid-only columns
 * the aggregation adds. SERVER SCHEMA GAP — reported.
 */
interface FormsListRow {
  _id: ObjectIdString;
  counter?: number;
  title?: string;
  description?: string;
  status?: string;
  resourceType?: string;
  projectId?: FormInstance["projectId"];
  contractId?: FormInstance["contractId"];
  space?: FormInstance["space"];
  selectedForm: FormInstance["selectedForm"];
  selectedFormFlow?: FormInstance["selectedFormFlow"];
  createdBy?: FormInstance["createdBy"];
  createdDate?: number;
  dueDate?: FormInstance["dueDate"];
  lastAction?: FormInstance["lastAction"];
  isArchived?: FormInstance["isArchived"];
  currentLevelName?: string;
  contractOrgName?: string;
  contractName?: string;
  contractType?: string;
}

/**
 * One column of the forms grid.
 *
 * Declared locally rather than via `typedColumns<FormsListRow>` for two reasons: several columns
 * address nested values by dotted path (`"space.spaceName"`, `"lastAction.userName"`), which
 * `ColumnDefinition`'s `field: keyof T` cannot express; and wrapping the array in `typedColumns()`
 * would add a call that is not in the emitted code today. The array is bridged to the grid's branded
 * column type at the prop instead.
 */
interface FormsGridColumn {
  field: string;
  id?: string;
  name?: string;
  width?: number;
  minWidth?: number;
  filterType?: "Text" | "Number" | "Date";
  _valueGetter?: (row: FormsListRow) => unknown;
  formatter?: (row: number, cell: number, value: unknown, columnDef: unknown, dataContext: FormsListRow) => unknown;
}

/**
 * A toolbar entry for this grid. `ToolbarItem` declares `text`, but `ToolBar.tsx` renders
 * `item.name` for these built-in buttons, so `name` is added rather than renamed — nothing about the
 * rendered toolbar changes.
 */
type FormsToolbarItem = ToolbarItem & { name?: string };

/** The slice of the SlickGrid1 imperative handle this grid drives. */
interface FormsGridHandle {
  slickGrid: { getSelectedRows: () => number[]; getDataItem: (row: number) => FormsListRow };
  refresh: () => void;
}

/** The SlickGrid selection event as this grid reads it. */
interface FormsSelectionEvent {
  detail: { args: { rows: number[] } };
}

/** The list query this grid drives SlickGrid1 with, before any caller override. */
interface FormsListQuery {
  projectId: ObjectIdString;
  resourceType: string;
  contractId?: string;
  contractVersionId?: string;
  params: {
    projectId: ObjectIdString;
    resourceType: string;
    contractId?: string;
    contractVersionId?: string;
  };
  where: Array<Record<string, unknown>>;
}

interface FormsProps {
  formCategory: string;
  source?: string;
  showOnlyApproved?: boolean;
  /**
   * A caller-supplied replacement for `initialQuery`. Deliberately `object`, not a query type: the
   * two callers build their own differently-shaped query objects (`useLinkedForm`'s `FormsQuery`,
   * the reviews section's `{ where }`), and this component only forwards it to SlickGrid1.
   */
  customQuery?: object;
  showOnlyGrid?: boolean;
  allowRecordClick?: boolean;
  showNavigateColumn?: boolean;
  setIsRowSelected?: (selected: boolean) => void;
  // setCount?: any;
}
const TasksForms = forwardRef(
  (
    {
      formCategory,
      source = "",
      showOnlyApproved,
      customQuery,
      showOnlyGrid = false,
      allowRecordClick = true,
      showNavigateColumn = false,
      setIsRowSelected,
      // setCount,
    }: FormsProps,
    ref,
  ) => {
    const { t } = useTranslation();
    // Typed, not validated: the backend route schema validates this id and 400s if malformed.
    const params = useParams() as { projectId: ObjectIdString; contractId?: string };
    const navigate = useNavigate();
    const userDetails = useSelector((state: RootState) => state.userDetails);
    const contractDetails = useSelector((state: RootState) => state.contractDetails);
    const [showFormFlowCreationModal, setShowFormFlowCreationModal] = useState({
      status: false,
      category: formCategory,
    });
    const [showArchivedForms, setShowArchivedForms] = useState(false);

    const [toolBarBtnState, setToolBarBtnState] = useState({
      delete: false,
      archive: false,
      unArchive: false,
    });

    const {
      userSettingsExists,
      resetConfig,
      showWarning,
      showWarningModal,
      hideWarningModal,
      resetConfigClick,
      moduleTxt,
      // Types flow from `useFromList`'s own return rather than being restated here.
    } = useFromList({
      projectId: params.projectId,
      formCategory: formCategory,
      showOnlyGrid,
    });

    const tempRef = useRef<FormsGridHandle | null>(null);
    // `ref` is a `ForwardedRef`, which may also be a callback; every caller passes a ref object, and
    // the assertion is erased at emit so the `||` fallback behaves exactly as before.
    const dataGrid = (ref || tempRef) as React.RefObject<FormsGridHandle | null>;
    const dispatch = useDispatch();

    const isModuleDisabled = params.contractId && !contractDetails?.modulesEnabled[formCategory];

    const initialQuery: FormsListQuery = {
      projectId: params.projectId,
      resourceType: formCategory,
      contractId: params.contractId,
      contractVersionId: contractDetails?.versionId,
      params: {
        projectId: params.projectId,
        resourceType: formCategory,
        contractId: params.contractId,
        contractVersionId: contractDetails?.versionId,
      },
      where: [
        {
          isComplex: false,
          field: "isArchived.status",
          operator: "equal",
          ignoreCase: false,
          value: showArchivedForms,
        },
      ],
    };

    if (source == "contract" && showOnlyApproved) {
      initialQuery.where = [
        {
          isComplex: true,
          ignoreCase: false,
          condition: "and",
          predicates: [
            {
              isComplex: false,
              field: "isArchived.status",
              operator: "equal",
              ignoreCase: false,
              value: showArchivedForms,
            },
            {
              isComplex: false,
              field: "isApproved",
              operator: "equal",
              ignoreCase: false,
              value: true,
            },
          ],
        },
      ];
    }

    const [query, setQuery] = useState(customQuery || initialQuery);

    useEffect(() => {
      const updatedQuery = customQuery || initialQuery;
      setQuery(updatedQuery);
    }, [showArchivedForms, formCategory, customQuery]);

    const progressTemplate = (args: FormsListRow) => {
      let totalMandatoryFields = 0;
      let mandatoryFieldsFilled = 0;

      args.selectedForm.template.map((section) => {
        if (section.questionFields) {
          if (section.sectionCode != presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION) {
            section.questionFields.map((field) => {
              const answerValue = field.answer?.value;
              if (field.isMandatory) {
                totalMandatoryFields = totalMandatoryFields + 1;
                if (
                  !(
                    (["", undefined, null, [], 0] as unknown[]).includes(answerValue) ||
                    (Array.isArray(answerValue) && answerValue?.join("")?.trim() == "")
                  )
                ) {
                  mandatoryFieldsFilled = mandatoryFieldsFilled + 1;
                }
              }
            });
          }
        }
      });
      const formCompletionPercent = Number(((mandatoryFieldsFilled * 100) / totalMandatoryFields).toFixed(2)) || 0;

      return `${formCompletionPercent}%`;
    };

    const handleFormOpenClick = (e: React.MouseEvent, args: FormsListRow) => {
      e.stopPropagation();

      const location = getInspectionUrl({
        resourceType: args.resourceType,
        resourceId: args._id,
        projectId: args.projectId,
        contractId: args.contractId,
      });

      window.open(location, "_blank");
    };

    const navigateTemplate = (args: FormsListRow) => {
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className="cursor-pointer">
          <IconComponent
            onClick={(e: React.MouseEvent) => handleFormOpenClick(e, args)}
            className="cursor-pointer"
            name="openInViewer"
            width="1.25rem"
            height="1.25rem"
          />
        </div>,
      );
      return container;
    };
    const statusTemplate = (args: FormsListRow) => {
      let status = "";
      let color;
      const stage = args.status;
      if (stage == formStatuses.inProgress) {
        status = "In progress";
        color = "bg-system_colour_12";
      }
      if (stage == formStatuses.submitted) {
        status = "Submitted";
        color = "bg-system_colour_45";
      }
      if (stage == formStatuses.approved) {
        status = "Approved";
        color = "bg-positive_communication";
      }
      if (stage == formStatuses.rejected) {
        status = "Rejected";
        color = "bg-system_colour_8";
      }
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className={`h-8 ${color} w-fit ps-3 gap-2 bg-opacity-30 rounded-2xl pe-3 flex items-center`}>
          <span className={`${color} rounded-full h-3 w-3`} />
          <span className="font-semibold">{status}</span>
        </div>,
      );
      return container;
    };

    function formatCreatedDate(data: FormsListRow, type: "createdDate" | "dueDate" | "lastAction") {
      let date;
      if (type === "lastAction") {
        // KNOWN BUG, preserved verbatim: `lastAction` is `.optional()` on the wire but is dereferenced
        // unconditionally here, so a row that has never been acted on throws TypeError while the grid
        // formats this cell. Asserted (erased at emit) to keep the existing runtime; the real fix is a
        // guard plus an empty-state path — a behaviour change, out of scope for a typing pass.
        if (data.lastAction!.timeStamp == null) {
          return "-";
        }
        date = data.lastAction!.timeStamp;
      } else {
        date = data[type];
      }
      let showDueDateMarker = false;
      let content = untranslated("");
      let color = "";
      if (type == "dueDate") {
        const today = Date.now();
        const threeDaysFromNow = today + 259200000;
        if (date <= threeDaysFromNow) {
          showDueDateMarker = true;
          const days = ((date - today) / (1000 * 60 * 60 * 24)).toFixed(2);
          content = t("Due date approaching in {{days}} days", { days });
          color = "#ffcf55";
        }
        if (date <= today) {
          showDueDateMarker = true;
          const days = ((today - date) / (1000 * 60 * 60 * 24)).toFixed(2);
          content = t("Due date exceeded by {{days}} days", { days });
          color = "#e75555";
        }
      }
      const dateFormat = convertDate(Number(date), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className="flex items-center gap-3">
          <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
            <span>{convertDate(Number(date), "dateForText")}</span>
          </ToolTipComponent>
          {showDueDateMarker && (
            <IconComponent
              showGradient
              color={color}
              name="details"
              width="1.5rem"
              height="1.5rem"
              position="top"
              toolTipContent={content}
            />
          )}
        </div>,
      );
      return container;
    }

    function createdByTemplate(params: FormsListRow, type: "createdBy" | "lastAction") {
      let _id;
      if (type === "lastAction") {
        // KNOWN BUG, preserved verbatim, as above: `lastAction` is wire-optional and `createdBy._id`
        // is likewise `.optional()`, yet both are read unconditionally here. Asserted (erased at emit).
        if (params.lastAction!.userId == "") {
          return "-";
        }
        _id = params.lastAction!.userId;
      } else {
        _id = params.createdBy!._id;
      }

      const container = document.createElement("div");
      renderUserTag(container, { id: _id });
      return container;
    }

    const columnDefinitions = useRef<FormsGridColumn[]>([
      ...(showNavigateColumn
        ? [
            {
              field: "_id",
              name: "",
              minWidth: 100,
              formatter: (_data, _params, _a, _b, value) => navigateTemplate(value),
            },
          ]
        : []),
      {
        field: "counter",
        id: "counter",
        name: t("Form No."),
        filterType: "Number",
        minWidth: 100,
      },
      {
        field: "title",
        id: "title",
        filterType: "Text",
        name: t("Title"),
        minWidth: 150,
      },
      {
        field: "description",
        id: "description",
        filterType: "Text",
        name: t("Description"),
        minWidth: 120,
      },
      {
        field: "space.spaceName",
        id: "spaceName",
        filterType: "Text",
        name: t("Space Name"),
        minWidth: 150,
        _valueGetter: (row) => row?.space?.spaceName,
        // `space` and `lastAction` are `.optional()` on the wire but the formatters below read them
        // unconditionally, exactly as they do today. Asserted (erased at emit).
        formatter: (_data, _params, _a, _b, value) => value.space!.spaceName,
      },
      {
        field: "status",
        id: "status",
        name: t("Status"),
        width: 150,
        filterType: "Text",
        minWidth: 150,
        _valueGetter: (row) => row?.status,
        formatter: (_data, _params, _a, _b, value) => statusTemplate(value),
      },
      {
        field: "selectedForm.name",
        id: "selectedForm.name",
        name: t("Template Attached"),
        minWidth: 150,
        filterType: "Text",
        _valueGetter: (row) => row?.selectedForm?.name,
      },
      {
        field: "selectedForm.type.name",
        id: "selectedForm.type.name",
        filterType: "Text",
        name: t("Attached Template Type"),
        minWidth: 180,
      },
      {
        field: "selectedFormFlow.name",
        id: "selectedFormFlow.name",
        name: t("Form Flow"),
        minWidth: 120,
        filterType: "Text",
      },
      {
        field: "createdBy.fullName",
        id: "createdBy.fullName",
        name: t("Raised by"),
        minWidth: 150,
        filterType: "Text",
        _valueGetter: (row) => row?.createdBy?.fullName,
        formatter: (_data, _params, _a, _b, value) => createdByTemplate(value, "createdBy"),
      },
      {
        field: "createdDate",
        id: "createdDate",
        name: t("Raised On"),
        minWidth: 120,
        filterType: "Date",
        formatter: (_data, _params, _a, _b, value) => formatCreatedDate(value, "createdDate"),
      },
      {
        field: "dueDate",
        id: "dueDate",
        name: t("Due Date"),
        minWidth: 200,
        filterType: "Date",
        formatter: (_data, _params, _a, _b, value) => (value.dueDate ? formatCreatedDate(value, "dueDate") : "-"),
      },
      {
        field: "formProgress",
        id: "formProgress",
        name: t("Form Progress"),
        minWidth: 150,
        formatter: (_data, _params, _a, _b, value) => progressTemplate(value),
      },
      {
        field: "currentLevelName",
        id: "currentLevelName",
        name: t("Current Level"),
        filterType: "Text",
        minWidth: 150,
      },
      {
        field: "lastAction.userName",
        id: "lastAction.userName",
        name: t("Last Action By"),
        minWidth: 150,
        filterType: "Text",
        formatter: (_data, _params, _a, _b, value) => createdByTemplate(value, "lastAction"),
      },
      {
        field: "lastAction.actionName",
        id: "lastAction.actionName",
        name: t("Last Action"),
        minWidth: 120,
        filterType: "Text",
        formatter: (_data, _params, _a, _b, value) => value.lastAction!.actionName,
      },
      {
        field: "lastAction.timeStamp",
        id: "lastAction.timeStamp",
        name: t("Last Action Time"),
        minWidth: 150,
        formatter: (_data, _params, _a, _b, value) => formatCreatedDate(value, "lastAction"),
      },
      {
        field: "contractOrgName",
        id: "contractOrgName",
        name: t("Org Name"),
        minWidth: 150,
      },
      {
        field: "contractName",
        id: "contractName",
        name: t("Contract Name"),
        minWidth: 150,
      },
      {
        field: "contractType",
        id: "contractType",
        name: t("Contract Type"),
        minWidth: 150,
      },
    ]);

    const handleChange = () => {
      setShowArchivedForms((prev) => !prev);
    };

    const dropDownTemplate = () => (
      <SelectDropDown
        select={handleChange}
        cssClass="e-caret-hide"
        bgColor="transparent"
        items={
          showArchivedForms
            ? [{ text: t("Show Active Forms"), id: "showArchive" }]
            : [{ text: t("Show Archived Forms"), id: "showActive" }]
        }
      >
        <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
      </SelectDropDown>
    );

    const archivedChipTemplate = () => (
      <span className="rounded-2xl ps-4 pe-4 pt-1 pb-1 text-sm bg-text_light text-white">
        <Text>{t("Archived Forms")}</Text>
      </span>
    );

    const formConfigTemplate = () => (
      <Box
        onClick={() => toolbarClickHandler("reset_form_config")}
        className="flex cursor-pointer items-center pe-3 gap-2"
      >
        <IconComponent name="rollbackReviewIcon" width="1rem" height="1rem" />
        <Text>{t("Reset Form Configuration")}</Text>
        {userSettingsExists && (
          <ToolTipComponent
            showTipPointer={true}
            position={"top"}
            content={t(
              "User settings exists, Meaning form will directly be created on clicking of create button skipping all form creation steps. To reset click here ",
            )}
          >
            <Badge color="primary" variant="dot" />
          </ToolTipComponent>
        )}
      </Box>
    );

    const toolbarOptions: FormsToolbarItem[] = [
      ...(formCategory != formCategories.changeOrder && (source == "project" || params.contractId) && !showArchivedForms
        ? [
            {
              name: t("Create"),
              prefixIcon: "add",
              id: "custom_create",
            },
          ]
        : []),
      ...(!showArchivedForms
        ? [
            {
              name: t("Archive"),
              prefixIcon: "archive",
              id: "custom_archive",
              disabled: !toolBarBtnState.archive,
            },
            {
              name: t("Delete"),
              prefixIcon: "delete",
              id: "custom_delete",
              disabled: !toolBarBtnState.delete,
            },
          ]
        : []),
      ...(showArchivedForms
        ? [
            {
              name: t("Un-Archive"),
              prefixIcon: "unarchive",
              id: "custom_unArchive",
              disabled: !toolBarBtnState.unArchive,
            },
          ]
        : []),
      ...(showArchivedForms
        ? [
            {
              template: archivedChipTemplate,
              align: "Left" as const,
            },
          ]
        : []),
      ...(formCategory != formCategories.changeOrder
        ? [
            {
              name: t("Reset Form Configuration"),
              template: formConfigTemplate,
              id: "reset_form_config",
              rightAllign: true,
            },
          ]
        : []),
      {
        template: dropDownTemplate,
        rightAllign: true,
      },
    ];

    const handleDeleteForms = async () => {
      // Non-null on the ref throughout this file: every handler is reached from the grid's own toolbar
      // or rows, so the grid is mounted before any of them can fire. Erased at emit — an unattached ref
      // still throws exactly as it does today.
      const selectedItem = dataGrid
        .current!.slickGrid.getSelectedRows()
        .map((data) => dataGrid.current!.slickGrid.getDataItem(data));
      const resourceIds = selectedItem.map((data) => data._id);
      const res = await taskInspectionAndProgressFormsController({
        action: "delete",
        resourceIds,
        resourceType: formCategory,
      });
      if (res.data.status) {
        hideWarningModal();
        dispatch(
          showToastNotification({
            // `taskInspectionAndProgressFormsControllerResponse` declares neither `message` nor
            // `warningMessage`, but the delete path returns both (a partial delete reports which forms
            // were skipped). SERVER SCHEMA GAP — reported; the receiver is asserted at each read (the
            // optional chains stay outside the parentheses) so the emitted expression is unchanged.
            // The cast wraps the whole `??` chain (each optional chain stays intact inside it, so
            // short-circuiting is unchanged): these are server strings and a template literal, none of
            // them translated, while the toast slice wants a `TranslatedString`.
            content: ((res.data as { message?: string })?.message ??
              (res.data as { warningMessage?: string })?.warningMessage ??
              `Selected ${moduleTxt} have been deleted successfully`) as TranslatedString,
            type: (res.data as { warningMessage?: string })?.warningMessage ? TOAST_TYPE_WARNING : TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid?.current?.refresh();
      } else {
        hideWarningModal();
        dispatch(
          showToastNotification({
            // `errorMessage` is a server string, not a translated key; the toast slice wants a
            // `TranslatedString`. Asserted (erased at emit) rather than wrapping in `untranslated()`,
            // which would add a call that is not in the emitted code today.
            content: res.data?.errorMessage as TranslatedString,
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    };

    const changeFormState = async (state: "custom_archive" | "custom_unArchive") => {
      let status;
      let message;
      if (state == "custom_archive") {
        status = true;
        message = "Archived";
      } else {
        status = false;
        message = "Un-Archived";
      }
      const allselectedRows = dataGrid
        .current!.slickGrid.getSelectedRows()
        .map((data) => dataGrid.current!.slickGrid.getDataItem(data));

      const allIds = allselectedRows.map((data) => data._id);
      const res = await taskInspectionAndProgressFormsController({
        action: "archive",
        resourceIds: allIds,
        data: { isArchived: { status } },
      });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Selected form(s) successfully {{message}}", { message: message }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current!.refresh();
      }
    };

    const toolbarClickHandler = async (args: string) => {
      if (args === "custom_delete") {
        showWarningModal("delete");
      }
      if (args === "custom_create") {
        if (userSettingsExists) {
          const res = await formUserSettingsController({
            action: "createForm",
            projectId: params.projectId,
            resourceType: formCategory,
          });
          if (res.data.status) {
            // `formUserSettingsControllerResponse` declares neither `id` nor `contractId`, but the
            // `createForm` action returns both (they are how the caller navigates to the new form).
            // SERVER SCHEMA GAP — reported; the receiver is asserted at each read so the emitted
            // expression is unchanged.
            const location = getInspectionUrl({
              resourceId: (res.data as { id?: string }).id,
              projectId: params.projectId,
              resourceType: formCategory,
              contractId: (res.data as { contractId?: string }).contractId,
            });
            navigate(location);
            dispatch(
              showToastNotification({
                content: t("Form created successfully"),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          } else {
            dispatch(
              showToastNotification({
                // See the note on the other `errorMessage` toast above: server string, asserted (erased).
                content: res.data.errorMessage as TranslatedString,
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        } else {
          setShowFormFlowCreationModal({
            status: true,
            category: formCategory,
          });
        }
      }
      if (args === "custom_archive") {
        changeFormState("custom_archive");
      }
      if (args === "custom_unArchive") {
        changeFormState("custom_unArchive");
      }
      if (args === "reset_form_config") {
        resetConfigClick();
      }
    };

    const getInspectionFormFLow = () => (
      <InspectionCreation
        projectId={params.projectId}
        showFormFlowCreationModal={showFormFlowCreationModal}
        setShowFormFlowCreationModal={setShowFormFlowCreationModal}
        source={params.contractId ? "contract" : "project"}
        spaceId={params.contractId ? contractDetails.space?._id : null}
      />
    );

    const selectionEvent = (args: FormsSelectionEvent) => {
      if (!dataGrid?.current) return;

      const totalRowsSelected = args.detail.args.rows.length;
      if (setIsRowSelected) {
        setIsRowSelected(totalRowsSelected > 0);
      }
      if (showOnlyGrid) {
        return;
      }
      if (totalRowsSelected == 0) {
        setToolBarBtnState({
          delete: false,
          archive: false,
          unArchive: false,
        });
      }
      if (totalRowsSelected >= 1) {
        const btnState = {
          delete: true,
          archive: true,
          unArchive: true,
        };
        const allselectedRows = dataGrid
          .current!.slickGrid.getSelectedRows()
          .map((data) => dataGrid.current!.slickGrid.getDataItem(data));

        allselectedRows.map((record) => {
          // `createdBy` is `.optional()` on the wire but read unconditionally, as in the templates
          // above. Asserted (erased at emit).
          if (record.createdBy!._id != userDetails.id) {
            btnState.delete = false;
          }
        });
        setToolBarBtnState({
          ...btnState,
        });
      }
    };

    const recordClick = (args: GridClickEvent) => {
      if (showOnlyGrid) {
        return;
      }
      const rowIndex = args.detail.args.row;
      const clickedRow = dataGrid.current!.slickGrid.getDataItem(rowIndex);

      if (args.detail.args.cell != 0 && !clickedRow.isArchived?.status && source !== "forms" && allowRecordClick) {
        const location = getInspectionUrl({
          resourceType: clickedRow.resourceType,
          resourceId: clickedRow._id,
          projectId: clickedRow.projectId,
          contractId: clickedRow.contractId,
        });
        navigate(location);
      }
    };

    // const created = () => {
    //   setGridReady(true);
    // };

    // const dataBound = () => {
    //   if (setCount) {
    //     setCount(dataGrid.current?.pageSettings?.properties?.totalRecordsCount);
    //   }
    // };

    const renderOption = (props: unknown, option: string, currentColumnId: string) => {
      if (currentColumnId == "status") {
        return formStatusMapper[option];
      }
    };

    const getGridComponent = () => (
      <SlickGrid1
        key={formCategory}
        ref={dataGrid as unknown as Parameters<typeof SlickGrid1<FormsListRow>>[0]["ref"]}
        remoteDataSource={taskInspectionAndProgressFormUrl}
        query={query as Parameters<typeof SlickGrid1<FormsListRow>>[0]["query"]}
        toolbar={showOnlyGrid ? [] : toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        // Both handlers are declared above with the shapes they actually read; SlickGrid1 types
        // `onSelectedRowIdsChanged` as `(args: Record<string, unknown>) => void` and `renderOption` as
        // a one-argument function, so each is bridged here. Erased at emit.
        onSelectedRowIdsChanged={selectionEvent as unknown as (args: Record<string, unknown>) => void}
        infiniteScroll
        renderOption={renderOption as unknown as (option: unknown) => React.ReactNode}
        onClick={recordClick}
        enableSortingOption
        columnDefinitions={columnDefinitions.current as unknown as ValidatedColumnDefinition<FormsListRow>[]}
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        enableViews={!showOnlyGrid && Boolean(params.projectId)}
        {...(!showOnlyGrid &&
          Boolean(params.projectId) && {
            viewsOptions: {
              headerName: "Views",
              name: "View",
              resourceType: formCategory,
              selectedLayoutKey: `${formCategory}_SELECTED_LAYOUT`,
              fetchParams: { projectId: params.projectId },
            },
          })}
      />
    );
    const warningModal = () => {
      const source = showWarning.source;
      let btnText;
      let contentTxt;
      let headerText;
      let callBackFun;
      switch (source) {
        case "delete":
          btnText = "Delete";
          contentTxt = `${
            formCategory == formCategories.changeOrder ? "All Associated changes in the contract will also be lost" : ""
          } Are you sure you want to delete all selected ${moduleTxt} ?`;
          headerText = t("Delete {{module}}", { module: moduleTxt });
          callBackFun = handleDeleteForms;
          break;
        case "resetConfig":
          btnText = "Reset";
          contentTxt = "Are you sure you want to reset the saved configuration.?";
          headerText = t("Reset Configuration");
          callBackFun = resetConfig;
          break;
      }
      return (
        <ModalStateDialogComponent
          footerContent={
            <div className="flex gap-5 items-center">
              <ButtonComponent onClick={callBackFun} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
                {btnText}
              </ButtonComponent>
              <ButtonComponent onClick={hideWarningModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
          width="30%"
          headerTitle={headerText}
          onCancel={hideWarningModal}
          isVisible={showWarning.status}
          modelContent={<div className="">{contentTxt} </div>}
        />
      );
    };

    return (
      <div className="h-full w-full overflow-auto flex">
        {showWarning.status && warningModal()}
        {showFormFlowCreationModal.status && getInspectionFormFLow()}
        {isModuleDisabled ? <DisabledIllustration /> : getGridComponent()}
      </div>
    );
  },
);
TasksForms.displayName = "TasksForms";
export default TasksForms;
